import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Calendar, Clock, MapPin, ArrowLeft, Search, FileText, Award, FileCheck } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { goBack } from '../../utils/navigation';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ---- Helpers de fecha ----
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  const parts = dateStr.split('T')[0].split('-');
  if (parts.length < 3) return null;
  return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
};

const formatDate = (dateStr) => {
  const d = parseLocalDate(dateStr);
  if (!d) return '';
  return d.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

// Obtiene la fecha "relevante" de un curso:
// - Si hay fechas futuras en el array, devuelve la más próxima (y flag future=true)
// - Si no, devuelve la fecha pasada más reciente (future=false)
// - Si no hay fechas, null
const getFechaRelevante = (curso) => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);

  const lista = [];
  if (curso.fechas && curso.fechas.length > 0) {
    curso.fechas.forEach(f => {
      const d = parseLocalDate(f);
      if (d) lista.push(d);
    });
  } else if (curso.fecha) {
    const d = parseLocalDate(curso.fecha);
    if (d) lista.push(d);
  }

  if (lista.length === 0) return { fecha: null, future: false, multiple: false };

  const futuras = lista.filter(d => d >= hoy).sort((a, b) => a - b);
  const pasadas = lista.filter(d => d < hoy).sort((a, b) => b - a);

  if (futuras.length > 0) {
    return { fecha: futuras[0], future: true, multiple: lista.length > 1 };
  }
  return { fecha: pasadas[0], future: false, multiple: lista.length > 1 };
};

const formatFechaRelevante = (fecha) => {
  if (!fecha) return '';
  return fecha.toLocaleDateString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};

// Determina el estado del curso: 'activo' | 'finalizado' | 'cerrado' | 'a_coordinar'
const getEstadoCurso = (curso) => {
  if (curso.horario_a_coordinar) return 'a_coordinar';
  const { fecha, future } = getFechaRelevante(curso);
  // Sin fecha y sin a coordinar: tratar como cerrado si inscripciones no abiertas, si no activo
  if (!fecha) {
    return curso.inscripciones_abiertas === false ? 'cerrado' : 'activo';
  }
  if (!future) return 'finalizado';
  if (curso.inscripciones_abiertas === false) return 'cerrado';
  return 'activo';
};

export default function CursosPublicosPage() {
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCursos();
  }, []);

  const fetchCursos = async () => {
    try {
      const res = await axios.get(`${API_URL}/capacitaciones/cursos`);
      setCursos(res.data);
    } catch (err) {
      console.error('Error cargando cursos:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredCursos = cursos.filter(curso =>
    curso.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    curso.descripcion_corta?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Ordenar cursos: próximos (activos / a coordinar) primero, pasados/cerrados al final.
  // Dentro del grupo activo: orden cronológico ascendente.
  // Dentro del grupo pasado: más reciente primero.
  const sortedCursos = [...filteredCursos].sort((a, b) => {
    const estadoA = getEstadoCurso(a);
    const estadoB = getEstadoCurso(b);

    const rank = {
      'activo': 0,
      'a_coordinar': 1,
      'cerrado': 2,
      'finalizado': 3,
    };

    if (rank[estadoA] !== rank[estadoB]) {
      return rank[estadoA] - rank[estadoB];
    }

    const fechaA = getFechaRelevante(a).fecha;
    const fechaB = getFechaRelevante(b).fecha;

    // Activos/cerrados futuros: ascendente (más próximo primero)
    if (estadoA === 'activo' || estadoA === 'cerrado') {
      if (fechaA && fechaB) return fechaA - fechaB;
      if (fechaA && !fechaB) return -1;
      if (!fechaA && fechaB) return 1;
      return 0;
    }

    // Finalizados: más reciente primero
    if (estadoA === 'finalizado') {
      if (fechaA && fechaB) return fechaB - fechaA;
    }

    return 0;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-colegio.png" alt="Logo" className="h-12 w-auto" />
            </Link>
            <button
              onClick={() => goBack()}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
              data-testid="cursos-volver-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Volver
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
            <GraduationCap className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Capacitaciones</h1>
          <p className="text-gray-600 max-w-xl mx-auto">
            Formaciones y propuestas del Colegio Profesional de Trabajadorxs Sociales
          </p>
        </div>

        {/* Search + Action Buttons */}
        <div className="max-w-3xl mx-auto mb-8">
          <div className="flex flex-col gap-4">
            {/* Search */}
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar capacitaciones..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="cursos-search-input"
              />
            </div>
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 justify-center">
              <Link to="/capacitaciones/emitir">
                <Button variant="outline" className="whitespace-nowrap" data-testid="emitir-constancia-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Emitir Constancia
                </Button>
              </Link>
              <Link to="/capacitaciones/emitir?tipo=certificado">
                <Button variant="outline" className="whitespace-nowrap" data-testid="emitir-certificado-btn">
                  <Award className="w-4 h-4 mr-2" />
                  Emitir Certificado
                </Button>
              </Link>
              <Link to="/verificar">
                <Button variant="outline" className="whitespace-nowrap text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid="verificar-certificado-btn">
                  <FileCheck className="w-4 h-4 mr-2" />
                  Verificar Certificado
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
          </div>
        )}

        {/* No Courses */}
        {!loading && sortedCursos.length === 0 && (
          <div className="text-center py-12">
            <GraduationCap className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">
              {searchTerm ? 'No se encontraron capacitaciones' : 'No hay capacitaciones disponibles en este momento'}
            </p>
          </div>
        )}

        {/* Courses Grid */}
        {!loading && sortedCursos.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedCursos.map((curso) => {
              const estado = getEstadoCurso(curso);
              const fechaInfo = getFechaRelevante(curso);
              const inactivo = estado === 'finalizado' || estado === 'cerrado';

              return (
                <Link
                  key={curso.id}
                  to={`/capacitaciones/${curso.slug || curso.id}`}
                  className={`bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden border border-gray-100 group ${inactivo ? 'opacity-75' : ''}`}
                  data-testid={`curso-card-${curso.slug || curso.id}`}
                >
                  {/* Image */}
                  <div className="aspect-video bg-gray-100 overflow-hidden relative">
                    {curso.imagen ? (
                      <img
                        src={curso.imagen}
                        alt={curso.nombre}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        style={{
                          objectPosition: curso.imagen_focal_point
                            ? `${curso.imagen_focal_point.x}% ${curso.imagen_focal_point.y}%`
                            : 'center'
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50">
                        <GraduationCap className="w-12 h-12 text-emerald-300" />
                      </div>
                    )}
                    {/* Badges sobre la imagen */}
                    <div className="absolute top-2 right-2 flex flex-col gap-1 items-end">
                      {estado === 'finalizado' && (
                        <Badge className="bg-gray-600 text-white text-xs" data-testid={`badge-finalizado-${curso.id}`}>
                          Finalizado
                        </Badge>
                      )}
                      {estado === 'cerrado' && (
                        <Badge className="bg-gray-500 text-white text-xs" data-testid={`badge-cerrado-${curso.id}`}>
                          Inscripciones cerradas
                        </Badge>
                      )}
                      {estado === 'a_coordinar' && (
                        <Badge className="bg-emerald-500 text-white text-xs" data-testid={`badge-coordinar-${curso.id}`}>
                          A coordinar
                        </Badge>
                      )}
                      {curso.tipo_curso && curso.tipo_curso !== 'CURSO' && (
                        <Badge variant="outline" className="bg-white/90 text-gray-700 text-xs">
                          {curso.tipo_curso === 'OTRO'
                            ? (curso.tipo_curso_otro || 'Otro')
                            : curso.tipo_curso.charAt(0) + curso.tipo_curso.slice(1).toLowerCase()}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="font-bold text-lg text-gray-900 mb-2 line-clamp-2">
                      {curso.nombre}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {curso.descripcion_corta}
                    </p>

                    {/* Meta */}
                    <div className="space-y-2 text-sm text-gray-500">
                      {curso.horario_a_coordinar ? (
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-emerald-600" />
                          <span className="text-emerald-700 font-medium">Horario a coordinar</span>
                        </div>
                      ) : fechaInfo.fecha ? (
                        <div className="flex items-center gap-2" data-testid={`curso-fecha-${curso.id}`}>
                          <Calendar className="w-4 h-4" />
                          <span>
                            {fechaInfo.multiple && (fechaInfo.future ? 'Próxima: ' : 'Última: ')}
                            {formatFechaRelevante(fechaInfo.fecha)}
                            {curso.hora && fechaInfo.future && ` - ${curso.hora} hs`}
                          </span>
                        </div>
                      ) : null}
                      {curso.duracion_horas && (
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>{curso.duracion_horas} horas</span>
                        </div>
                      )}
                      {curso.modalidad && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{curso.modalidad}</span>
                        </div>
                      )}
                      {curso.lugar && (curso.modalidad === 'Presencial' || curso.modalidad === 'Híbrida') && (
                        <div className="flex items-start gap-2" data-testid={`curso-lugar-${curso.id}`}>
                          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-emerald-600" />
                          <span className="text-gray-700 line-clamp-2">{curso.lugar}</span>
                        </div>
                      )}
                    </div>

                    {/* Button */}
                    <div className="mt-4 pt-4 border-t">
                      {estado === 'finalizado' ? (
                        <span className="text-gray-400 font-semibold text-sm">
                          Finalizado
                        </span>
                      ) : estado === 'cerrado' ? (
                        <span className="text-gray-400 font-semibold text-sm">
                          Inscripciones cerradas
                        </span>
                      ) : (
                        <span className="text-emerald-600 font-semibold text-sm group-hover:text-emerald-700">
                          Ver más →
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>Colegio Profesional de Trabajadorxs Sociales - Distrito Lomas de Zamora</p>
        </div>
      </footer>
    </div>
  );
}
